import { useEffect, useMemo, useState } from "react";
import { Badge, Container } from "reactstrap";
import { format, intervalToDuration } from "date-fns";

import ReactTable from "components/ReactTable/ReactTable";
import useFetch from "Hooks/useFetch";
import styles from "Pages/Admin/ClientHealth/ClientHealth.module.css";
import DefaultColumnFilter from "components/ReactTable/Components/DefaultColumnFilter/DefaultColumnFilter";
import useDuration from "Pages/Admin/ClientHealth/Hooks/Duration";
import useAlertContext from "Hooks/useAlertContext";
import usePageTitle from "Hooks/usePageTitle";

export default function ClientHealth() {
    const [healthSummaries, setHealthSummaries] = useState<
        IClientHealthSummary[]
    >([]);
    const [isLoading, setIsLoading] = useState(false);

    usePageTitle("Client Health");
    const { get } = useFetch();

    const { durationDropdown, duration } = useDuration();
    const { newAlert } = useAlertContext();

    const secondsStringToHMSDuration = (input_seconds: string) => {
        const seconds = parseInt(input_seconds, 10);

        const duration = intervalToDuration({
            start: 0,
            end: seconds * 1000,
        });

        const parts = [];
        if (duration.hours && duration.hours > 0) {
            parts.push(`${duration.hours}h`);
        }
        if (duration.minutes && duration.minutes > 0) {
            parts.push(`${duration.minutes}m`);
        }
        if (duration.seconds && duration.seconds > 0) {
            parts.push(`${duration.seconds}s`);
        }
        const formatted = parts.join(" ");

        return formatted;
    };

    useEffect(() => {
        let ignore = false;

        getCompanies();

        async function getCompanies() {
            setIsLoading(true);

            const response = await get(`api/admin/client_health/${duration}`);

            if (response.ok && !ignore) {
                const data = await response.json();
                console.log("Get client health response: ", data);

                setHealthSummaries(data);
            } else if (!response.ok && !ignore) {
                newAlert({ message: "Error getting client health data" });
                console.log(response);
            }

            setIsLoading(false);
        }

        return () => {
            ignore = true;
        };
    }, [get, duration, newAlert]);

    const columns = useMemo(() => {
        const runTimeFormat = (value: string) => {
            let timestamp = secondsStringToHMSDuration(value);
            return <span className={styles.textBox}>{timestamp}</span>;
        };

        const cellContent = (value: string, color: string) => {
            return (
                value !== "0" && (
                    <Badge color={color} style={{ alignSelf: "center" }} pill>
                        {value}
                    </Badge>
                )
            );
        };

        return [
            {
                Header: "ID",
                accessor: "company_id",
                width: 110,
                // Not using default filter, so as to allow for auto focusing this input
                Filter: ({ column }: any) => {
                    return <DefaultColumnFilter column={column} autoFocus />;
                },
            },
            {
                Header: "Name",
                accessor: "company_name",
            },
            {
                Header: "Systems",
                accessor: "company_systems",
                width: 100,
            },
            {
                Header: "Integrations Run",
                accessor: "count",
                width: 120,
                disableFilters: true,
            },
            {
                Header: "Errored to Message Header Ratio",
                accessor: "errored_ratio",
                disableFilters: true,
            },
            {
                Header: "Errors",
                accessor: "errors",
                width: 80,
                disableFilters: true,
                Cell: ({ value }: { value: string; color: string }) =>
                    cellContent(value, "danger"),
            },
            {
                Header: "Warnings",
                accessor: "warnings",
                width: 80,
                disableFilters: true,
                Cell: ({ value }: { value: string; color: string }) =>
                    cellContent(value, "warning"),
            },
            {
                Header: "Creates",
                accessor: "creates",
                width: 80,
                disableFilters: true,
                Cell: ({ value }: { value: string; color: string }) =>
                    cellContent(value, "info"),
            },
            {
                Header: "Updates",
                accessor: "updates",
                width: 80,
                disableFilters: true,
                Cell: ({ value }: { value: string; color: string }) =>
                    cellContent(value, "success"),
            },
            {
                Header: "Deletes",
                accessor: "deletes",
                width: 80,
                disableFilters: true,
                Cell: ({ value }: { value: string; color: string }) =>
                    cellContent(value, "secondary"),
            },
            {
                Header: "Max Run Time",
                accessor: "max_run_time",
                disableFilters: true,
                Cell: ({ value }: { value: string }) => runTimeFormat(value),
            },
        ];
    }, []);

    const tableProps = {
        columns,
        data: healthSummaries,
        showPagination: true,
        showContextMenu: true,
        isLoading,
    };

    return (
        <Container fluid>
            <div className={styles.headerAndButtonsBox}>
                <h1>Client Health Summary for the past {durationDropdown}</h1>
            </div>
            <div>
                <h2>As of {format(new Date(), "yyyy-MM-dd, H:mm")}</h2>
            </div>

            <ReactTable {...tableProps} />
        </Container>
    );
}
